<script lang="ts">
export type CodeExampleProps = {
  /**
   * Integration type: determines if the code sample is displayed in a client environment
   * or in an API reference environment.
   */
  integration?: 'client' | 'reference'
  /**
   * List of all http clients formatted into option groups for the client selector
   */
  clientOptions: ClientOptionGroup[]
  /**
   * Pre-selected client, this will determine which client is initially selected in the dropdown.
   * Either a built-in client id (e.g. `js/fetch`) or a custom sample id (e.g. `custom/python`).
   *
   * Typed as a plain string: unioning the (large) client-id union with the `custom/...`
   * ids overflows TypeScript's union-complexity limit inside this component's `defineProps`.
   *
   * @defaults to a custom sample if one is available, otherwise shell/curl
   */
  selectedClient?: string
  /**
   * Which server from the spec to use for the code example
   */
  selectedServer?: ServerObject | null
  /**
   * The selected content type from the requestBody.content, this will determine which examples are available
   * as well as the content type of the code example
   *
   * @defaults to the first content type if not provided
   */
  selectedContentType?: string
  /**
   * Example name to use for resolving example values for parameters AND requestBody.
   *
   * This is the document-wide selection: it is honored only when this operation defines an example
   * with the same key, so picking an example on one operation syncs the rest without blanking out
   * operations that do not share that key.
   *
   * @example "limited"
   * ```ts
   * parameters: {
   *   name: 'foobar',
   *   in: 'query',
   *   examples: {
   *     limited: {
   *       dataValue: 10,
   *     }
   *   }
   * },
   * body: {
   *   content: {
   *     'application/json': {
   *       examples: {
   *         limited: {
   *           dataValue: { foo: 'bar' },
   *         }
   *       }
   *     }
   *   }
   * }
   *
   * ```
   */
  selectedExample?: string
  /**
   * Event bus
   */
  eventBus: WorkspaceEventBus
  /**
   * The security schemes which are applicable to this operation
   */
  securitySchemes: SecuritySchemeObjectSecret[]
  /**
   * HTTP method of the operation
   */
  method: HttpMethodType
  /**
   * Path of the operation
   */
  path: string
  /**
   * De-referenced OpenAPI Operation object
   */
  operation: OperationObject
  /**
   * If true and there's no example, we will display a small card with the method and path only
   */
  fallback?: boolean
  /**
   * A method to generate the label of the block, should return an html string
   */
  generateLabel?: () => string
  /**
   * If true, render this as a webhook request example
   */
  isWebhook?: boolean
  /**
   * Workspace + document cookies
   */
  globalCookies?: XScalarCookie[]
  /**
   * When the request body schema uses oneOf/anyOf, use these selected variants
   * for the example snippet (e.g. from the schema dropdowns in the API reference).
   */
  requestBodyCompositionSelection?: Record<string, number>
}

/**
 * Request Example
 *
 * The core component for rendering a request example block,
 * this component does not have much of its own state but operates on props and custom events
 *
 * @event workspace:update:selected-client - Emitted when the selected client changes
 * @event workspace:update:selected-example - Emitted when the selected example changes, so other operations can sync
 */
export default {}
</script>

<script setup lang="ts">
import { ScalarButton } from '@scalar/components/button'
import {
  ScalarCard,
  ScalarCardFooter,
  ScalarCardHeader,
  ScalarCardSection,
} from '@scalar/components/card'
import { ScalarCodeBlock } from '@scalar/components/code-block'
import { ScalarCombobox } from '@scalar/components/combobox'
import { ScalarVirtualText } from '@scalar/components/virtual-text'
import { freezeElement } from '@scalar/helpers/dom/freeze-element'
import type { HttpMethod as HttpMethodType } from '@scalar/helpers/http/http-methods'
import { ScalarIconCaretDown } from '@scalar/icons'
import { type WorkspaceEventBus } from '@scalar/workspace-store/events'
import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref'
import type { SecuritySchemeObjectSecret } from '@scalar/workspace-store/request-example'
import type { XScalarCookie } from '@scalar/workspace-store/schemas/extensions/general/x-scalar-cookies'
import type {
  OperationObject,
  ServerObject,
} from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'
import {
  computed,
  onBeforeMount,
  ref,
  useId,
  watch,
  watchEffect,
  type ComponentPublicInstance,
} from 'vue'

import { filterClientsByQuery } from '../helpers/filter-clients-by-query'
import { findClient } from '../helpers/find-client'
import { generateCodeSnippet } from '../helpers/generate-code-snippet'
import { getClients } from '../helpers/get-clients'
import { getCustomCodeSamples } from '../helpers/get-custom-code-samples'
import { getSecrets } from '../helpers/get-secrets'
import { operationToHar } from '../helpers/operation-to-har/operation-to-har'
import type {
  ClientOption,
  ClientOptionGroup,
  CustomClientOption,
} from '../types'
import ExamplePicker from './ExamplePicker.vue'
import HttpMethod from './HttpMethod.vue'

const {
  integration,
  clientOptions,
  selectedClient,
  selectedServer = null,
  selectedContentType,
  selectedExample,
  securitySchemes = [],
  method,
  eventBus,
  path,
  operation,
  isWebhook,
  generateLabel,
  globalCookies,
  requestBodyCompositionSelection,
} = defineProps<CodeExampleProps>()

const emit = defineEmits<{
  /**
   * Emitted whenever the example key actually shown for this operation changes.
   *
   * This is the resolved local key (not the raw document-wide selection), so layouts that render
   * the test-request button outside this component can open the client with the same example the
   * snippet displays.
   */
  (e: 'update:exampleKey', value: string): void
}>()

defineSlots<{
  header: () => unknown
  footer: ({ exampleName }: { exampleName: string }) => unknown
}>()

/** Grab the examples for the given content type */
const requestBodyExamples = computed(() => {
  const content = getResolvedRef(operation.requestBody)?.content ?? {}
  const contentType = selectedContentType || Object.keys(content)[0]
  if (!contentType) return {}

  const examples = content[contentType]?.examples ?? {}

  return examples
})

/**
 * The example key actually shown for this operation.
 *
 * `selectedExample` is the document-wide selection that syncs across operations. We honor it only
 * when this operation actually defines that key, otherwise we keep the local example: a selection
 * on one operation should never blank out an operation that does not share the same example.
 */
const localExampleKey = ref('')

/** Resolve the key to display, preferring the document-wide selection when this operation has it */
const resolveExampleKey = (preferred: string | undefined): string => {
  const keys = Object.keys(requestBodyExamples.value)
  if (preferred && keys.includes(preferred)) {
    return preferred
  }
  // Keep the current local example when it is still valid, otherwise fall back to the first one
  if (localExampleKey.value && keys.includes(localExampleKey.value)) {
    return localExampleKey.value
  }
  return keys[0] ?? ''
}

// Set the initial example from the document-wide selection, falling back to the first example
onBeforeMount(() => {
  localExampleKey.value = resolveExampleKey(selectedExample)
})

// Follow the document-wide selection when it changes and this operation has that example
watch(
  () => selectedExample,
  (preferred) => {
    localExampleKey.value = resolveExampleKey(preferred)
  },
)

/** Reset the selected example key if the content type changes and the new content type doesn't have the previously selected example */
watch(
  () => selectedContentType,
  () => {
    if (
      !Object.keys(requestBodyExamples.value).includes(localExampleKey.value)
    ) {
      // Re-resolve so the new content type still follows the document-wide selection when it has
      // that key, instead of always snapping back to the first example
      localExampleKey.value = resolveExampleKey(selectedExample)
    }
  },
)

// Keep the parent informed of the resolved key so a test-request button rendered outside this
// component (e.g. the classic layout header) opens the client with the example the snippet shows
watchEffect(() => {
  emit('update:exampleKey', localExampleKey.value)
})

/** Select an example locally and sync the choice across the document */
const selectExample = (key: string) => {
  localExampleKey.value = key
  eventBus.emit('workspace:update:selected-example', key)
}

/** Grab any custom code samples from the operation */
const customCodeSamples = computed(() => getCustomCodeSamples(operation))

/** Merge custom code samples with the client options */
const clients = computed(() =>
  getClients(
    customCodeSamples.value.samples,
    clientOptions,
    customCodeSamples.value.label,
  ),
)

/** Total number of available clients across all option groups */
const clientCount = computed(() =>
  clients.value.reduce((total, group) => total + group.options.length, 0),
)

/** The locally selected client which would include code samples from this operation only */
const localSelectedClient = ref<ClientOption | CustomClientOption | undefined>(
  findClient(clients.value, selectedClient),
)

/**
 * Re-resolve the local client whenever the global selection or the available
 * clients change. Watching `clients` matters when navigating between operations:
 * the stored id stays the same, but the matching option (e.g. a custom sample)
 * differs per operation, so without this the snippet could go stale.
 */
watch([() => selectedClient, clients], ([newClient]) => {
  const client = findClient(clients.value, newClient)
  if (client) {
    localSelectedClient.value = client
  }
})

/** Generate HAR data for webhook requests */
const webhookHar = computed(() => {
  if (!isWebhook) return null

  try {
    return operationToHar({
      operation,
      method,
      path,
      example: localExampleKey.value,
      requestBodyCompositionSelection,
      // Only required parameters are shown in code examples; optional parameters
      // are omitted unless explicitly enabled via `x-disabled: false`.
      defaultDisabledParameters: true,
    })
  } catch (error) {
    console.error('[webhookHar]', error)
    return null
  }
})

/** Generate the code snippet for the selected example */
const generatedCode = computed<string>(() => {
  if (isWebhook) {
    return webhookHar.value?.postData?.text ?? ''
  }

  return generateCodeSnippet({
    // Only required parameters are shown in code examples; optional parameters
    // are omitted unless explicitly enabled via `x-disabled: false`.
    defaultDisabledParameters: true,
    includeDefaultHeaders: integration === 'client',
    clientId: localSelectedClient.value?.id,
    customCodeSamples: customCodeSamples.value.samples,
    operation,
    method,
    path,
    contentType: selectedContentType,
    server: selectedServer,
    securitySchemes,
    example: localExampleKey.value,
    globalCookies,
    requestBodyCompositionSelection,
  })
})

/** The language for the code block, used for syntax highlighting */
const codeBlockLanguage = computed(() => {
  if (isWebhook) {
    return webhookLanguage.value
  }

  return localSelectedClient.value?.lang
})

/** Determine the language for webhook content based on MIME type */
const webhookLanguage = computed<string>(() => {
  if (!webhookHar.value?.postData) return 'json'

  const contentType = webhookHar.value.postData.mimeType
  if (contentType?.includes('json')) return 'json'
  if (contentType?.includes('xml')) return 'xml'
  if (contentType?.includes('yaml') || contentType?.includes('yml'))
    return 'yaml'
  if (contentType?.includes('text/plain')) return 'text'

  return 'json'
})

/**  Block secrets from being shown in the code block */
const secretCredentials = computed(() => getSecrets(securitySchemes))

/** Grab the ref to freeze the ui as the clients change so there's no jump as the size of the dom changes */
const elem = ref<ComponentPublicInstance | null>(null)

/** Set custom example, or update the selected HTTP client globally */
const selectClient = (option: ClientOption) => {
  // We need to freeze the ui to prevent scrolling as the clients change
  if (elem.value) {
    const unfreeze = freezeElement(elem.value.$el)
    setTimeout(() => {
      unfreeze()
    }, 300)
  }
  // Update to the local example
  localSelectedClient.value = option

  // Sync the selection globally so other operations follow along. Custom samples
  // sync too (keyed by language), so picking e.g. the Python SDK example here
  // shows the Python example on every operation that ships one.
  if (option) {
    eventBus.emit('workspace:update:selected-client', option.id)
  }
}

// Virtualize the code block if it's too large
// This prevents the entire app from freezing up if there's a massive example
// We set a lower threshold here as code examples can get quite large
const VIRTUALIZATION_THRESHOLD = 20_000

const shouldVirtualize = computed(
  () => (generatedCode.value.length ?? 0) > VIRTUALIZATION_THRESHOLD,
)

const id = useId()
</script>
<template>
  <ScalarCard
    v-if="generatedCode"
    ref="elem"
    class="request-card dark-mode">
    <!-- Header -->
    <ScalarCardHeader class="pr-2.5">
      <span class="sr-only">Request Example for</span>
      <HttpMethod
        as="span"
        class="request-method"
        :method="method" />
      <span
        v-if="generateLabel"
        v-html="generateLabel()" />
      <slot name="header" />
      <!-- Client picker -->
      <template
        v-if="!isWebhook && clientCount"
        #actions>
        <!-- Multiple clients: render a dropdown to switch between them -->
        <ScalarCombobox
          v-if="clientCount > 1"
          class="max-h-80"
          :filterFn="filterClientsByQuery"
          :modelValue="localSelectedClient"
          :options="clients"
          placement="bottom-end"
          teleport
          @update:modelValue="selectClient($event as ClientOption)">
          <ScalarButton
            class="text-c-2 hover:text-c-1 flex h-full w-fit gap-1.5 px-0.5 py-0 text-base font-normal"
            data-testid="client-picker"
            variant="ghost">
            {{ localSelectedClient?.title }}
            <ScalarIconCaretDown
              class="ui-open:rotate-180 mt-px size-3 transition-transform duration-100"
              weight="bold" />
          </ScalarButton>
        </ScalarCombobox>
        <!-- Single client: just show its label, no need for a dropdown -->
        <span
          v-else
          class="text-c-2 flex h-full w-fit items-center px-0.5 py-0 text-base font-normal"
          data-testid="client-picker">
          {{ localSelectedClient?.title }}
        </span>
      </template>
    </ScalarCardHeader>

    <!-- Code snippet -->
    <ScalarCardSection class="request-editor-section custom-scroll p-0">
      <div
        :id="`${id}-example`"
        class="code-snippet">
        <ScalarCodeBlock
          v-if="!shouldVirtualize"
          class="bg-b-2 h-full"
          :content="generatedCode"
          :hideCredentials="secretCredentials"
          :lang="codeBlockLanguage"
          lineNumbers />
        <ScalarVirtualText
          v-else
          containerClass="custom-scroll scalar-code-block border rounded-b flex flex-1 max-h-screen"
          contentClass="language-plaintext whitespace-pre font-code text-base p-2"
          :lineHeight="20"
          :text="generatedCode" />
      </div>
    </ScalarCardSection>

    <!-- Footer -->
    <ScalarCardFooter
      v-if="Object.keys(requestBodyExamples).length > 1 || $slots.footer"
      class="request-card-footer bg-b-3">
      <!-- Example picker -->
      <div
        v-if="Object.keys(requestBodyExamples).length > 1"
        class="request-card-footer-addon">
        <template v-if="Object.keys(requestBodyExamples).length">
          <ExamplePicker
            :examples="requestBodyExamples"
            :modelValue="localExampleKey"
            @update:modelValue="selectExample" />
        </template>
      </div>

      <!-- Footer -->
      <slot
        :exampleName="localExampleKey"
        name="footer" />
    </ScalarCardFooter>
  </ScalarCard>

  <!-- Fallback card with just method and path in the case of no examples -->
  <ScalarCard
    v-else-if="fallback"
    class="request-card dark-mode">
    <ScalarCardSection class="request-card-simple">
      <div class="request-header">
        <HttpMethod
          as="span"
          class="request-method"
          :method="method" />
        <slot name="header" />
      </div>
      <slot
        :exampleName="localExampleKey"
        name="footer" />
    </ScalarCardSection>
  </ScalarCard>
</template>
<style scoped>
.request-card {
  color: var(--scalar-color-1);
  font-size: var(--scalar-font-size-3);
}
.request-method {
  font-family: var(--scalar-font-code);
  text-transform: uppercase;
  margin-right: 6px;
}
.request-card-footer {
  display: flex;
  justify-content: flex-end;
  padding: 6px;
  flex-shrink: 0;
  position: relative;
}
.request-card-footer-addon {
  display: flex;
  align-items: center;

  flex: 1;
  min-width: 0;
}
.request-editor-section {
  display: flex;
  flex: 1;
}
.request-card-simple {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 8px 8px 8px 12px;

  font-size: var(--scalar-small);
}
.code-snippet {
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
